import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader } from '@/components/ui/dialog';
import { DialogTitleWithBug } from '@/components/ui/dialog-title-with-bug';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Upload, X, Linkedin, Globe, Mail, Phone, Twitter } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { usePodcastGuestDialog } from '@/contexts/PodcastGuestDialogContext';
import { useBrandManager } from '@/hooks/useBrandManager';
import { useCreatePodcastGuest, useUpdatePodcastGuest } from '@/hooks/usePodcastGuests';
import { RichTextEditor } from '@/components/learn/admin/RichTextEditor';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { GUEST_STATUS_OPTIONS, PodcastGuestInsert } from '@/types/podcastHost';

interface FormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  job_title: string;
  bio: string;
  image_url: string;
  linkedin: string;
  twitter_handle: string;
  website: string;
  status: string;
  source: string;
}

const defaultFormData: FormData = {
  name: '',
  email: '',
  phone: '',
  company: '',
  job_title: '',
  bio: '',
  image_url: '',
  linkedin: '',
  twitter_handle: '',
  website: '',
  status: 'prospect',
  source: '',
};

export const GlobalPodcastGuestDialog = () => {
  const { isOpen, mode, editingGuest, closeDialog } = usePodcastGuestDialog();
  const { currentBrandId } = useBrandManager();
  const createGuest = useCreatePodcastGuest();
  const updateGuest = useUpdatePodcastGuest();
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const isCreate = mode === 'create';

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && editingGuest) {
        setFormData({
          name: editingGuest.name || '',
          email: editingGuest.email || '',
          phone: editingGuest.phone || '',
          company: editingGuest.company || '',
          job_title: editingGuest.job_title || '',
          bio: editingGuest.bio || '',
          image_url: editingGuest.image_url || '',
          linkedin: editingGuest.linkedin || '',
          twitter_handle: editingGuest.twitter_handle || '',
          website: editingGuest.website || '',
          status: editingGuest.status || 'prospect',
          source: editingGuest.source || '',
        });
      } else {
        setFormData(defaultFormData);
      }
    }
  }, [isOpen, mode, editingGuest]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setIsUploading(true);
    try {
      const fileExt = file.name.split('.').pop() || 'png';
      const fileName = `${currentBrandId}/guests/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('instructor-images')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('instructor-images')
        .getPublicUrl(fileName);

      setFormData({ ...formData, image_url: publicUrl });
      toast.success('Image uploaded successfully');
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(`Failed to upload image: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setFormData({ ...formData, image_url: '' });
  };

  const onSubmit = async () => {
    if (!formData.name) return;

    const guestData = {
      name: formData.name,
      email: formData.email || null,
      phone: formData.phone || null,
      company: formData.company || null,
      job_title: formData.job_title || null,
      bio: formData.bio || null,
      image_url: formData.image_url || null,
      linkedin: formData.linkedin || null,
      twitter_handle: formData.twitter_handle || null,
      website: formData.website || null,
      status: formData.status as any,
      source: formData.source || null,
      expertise_topics: [],
    };

    if (mode === 'create') {
      if (!currentBrandId) return;
      await createGuest.mutateAsync({
        ...guestData,
        brand_id: currentBrandId,
      } as PodcastGuestInsert);
    } else {
      if (!editingGuest) return;
      await updateGuest.mutateAsync({
        id: editingGuest.id,
        updates: guestData,
      });
    }

    closeDialog();
  };

  const isPending = createGuest.isPending || updateGuest.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitleWithBug 
            popupName={isCreate ? 'Create Guest' : 'Edit Guest'} 
            popupContext="/podcasthost/guests"
            isOpen={isOpen}
          >
            {isCreate ? 'Create New Guest' : 'Edit Guest'}
          </DialogTitleWithBug>
        </DialogHeader>

        <div className="space-y-6">
          <Tabs defaultValue="details" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="social">Social Links</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-6 mt-4">
              {/* Image */}
              <div>
                <Label>Profile Image</Label>
                <div className="space-y-4 mt-2">
                  <div className="flex items-center gap-4">
                    <Avatar className="h-20 w-20">
                      <AvatarImage src={formData.image_url || undefined} />
                      <AvatarFallback>
                        {formData.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'GU'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 space-y-2">
                      {formData.image_url ? (
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleRemoveImage}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Remove Image
                          </Button>
                          <p className="text-xs text-muted-foreground">Image uploaded</p>
                        </div>
                      ) : (
                        <>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileUpload}
                            className="hidden"
                            id="global-guest-image-upload"
                            disabled={isUploading}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => document.getElementById('global-guest-image-upload')?.click()}
                            disabled={isUploading}
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            {isUploading ? 'Uploading...' : 'Upload Image'}
                          </Button>
                          <p className="text-xs text-muted-foreground">
                            PNG, JPG up to 5MB
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                  <Input
                    placeholder="Or paste image URL"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    className="text-sm"
                  />
                </div>
              </div>

              {/* Name & Email */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input
                    placeholder="Guest name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email
                  </Label>
                  <Input
                    type="email"
                    placeholder="email@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
              </div>

              {/* Company & Job Title */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Company</Label>
                  <Input
                    placeholder="Company name"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Job Title</Label>
                  <Input
                    placeholder="CEO, Founder, etc."
                    value={formData.job_title}
                    onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                  />
                </div>
              </div>

              {/* Phone & Status */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Phone
                  </Label>
                  <Input
                    placeholder="+1 234 567 890"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(v) => setFormData({ ...formData, status: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {GUEST_STATUS_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Bio */}
              <div>
                <Label>Bio</Label>
                <RichTextEditor
                  content={formData.bio}
                  onChange={(content) => setFormData({ ...formData, bio: content })}
                  placeholder="Tell us about this guest..."
                />
              </div>
            </TabsContent>

            <TabsContent value="social" className="space-y-6 mt-4">
              {/* LinkedIn */}
              <div>
                <Label className="flex items-center gap-2">
                  <Linkedin className="h-4 w-4" />
                  LinkedIn
                </Label>
                <Input
                  placeholder="https://linkedin.com/in/..."
                  value={formData.linkedin}
                  onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                />
              </div>

              {/* Twitter/X */}
              <div>
                <Label className="flex items-center gap-2">
                  <Twitter className="h-4 w-4" />
                  Twitter/X Handle
                </Label>
                <Input
                  placeholder="@username"
                  value={formData.twitter_handle}
                  onChange={(e) => setFormData({ ...formData, twitter_handle: e.target.value })}
                />
              </div>

              {/* Website */}
              <div>
                <Label className="flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  Website
                </Label>
                <Input
                  placeholder="https://..."
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                />
              </div>

              {/* Source */}
              <div>
                <Label>Source</Label>
                <Input
                  placeholder="How did you find this guest?"
                  value={formData.source}
                  onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                />
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button onClick={onSubmit} disabled={!formData.name || isPending}>
              {isPending ? 'Saving...' : isCreate ? 'Create' : 'Save'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
